import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material';
import { TreeViewItemId } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { subscribe, unsubscribe } from 'pubsub-js';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { OBJECT_CHANGED_TOPIC, ObjectChangedMsg } from '../../../common/models/pub-sub/PubSubTopics';
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from '../../../contexts/user-context/UserContext';
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { INavigationItem } from '../../../services/navigation-data-service/Models';
import NavigationDataService from '../../../services/navigation-data-service/NavigationDataService';
import { GroupTreeItem } from './GroupTreeItem';

export type GroupHierarchyProps = {
    onSelected: (selectedItem: INavigationItem, shiftKey: boolean, ctrlKey: boolean) => void;
    enableDynamicGroupRefresh?: boolean;
    onLoading?: (isLoading: boolean) => void;
    defaultSelected?: number;
    hiddenGroups?: number[];
    rootID?: number;
    onExpanded?: () => void;
}

export default function GroupHierarchy({
    onSelected, 
    onLoading,
    enableDynamicGroupRefresh,
    defaultSelected,
    hiddenGroups,
    rootID,
    onExpanded
}: GroupHierarchyProps) {
    
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();

    const treeViewRef = useRef<HTMLUListElement>(null);

    const [treeData, setTreeData] = useState<INavigationItem>();
    const [selected, setSelected] = useState<string | undefined>(() => {
        
        return defaultSelected?.toString() ?? routeContext.groupID?.toString();
    });

    const [expanded, setExpanded] = useState<string[]>([]);
    const [groups, setGroups] = useState<INavigationItem[] | undefined>();

    const theme = useTheme();

    useEffect(() => {

        const getChildren = (groups: INavigationItem[], parent: INavigationItem) => {

            const children = groups.filter(o => o.parentID === parent.objectID);

            if (children) {
                parent.children = [];

                children.forEach((value: INavigationItem/*, index: number*/) => {
                    if (value.hasChildren) {
                        getChildren(groups, value);
                    }

                    // if (parent.objectID === 2341) {
                    //     console.log(JSON.stringify(value));
                    // }
                    parent.children?.push(value);
                });
            }
        }

        const getTreeData = async (customerID: number, groupID: number) => {

            let allGroups = await NavigationDataService.getNavigationGroups(customerID, offlineState.offline);

            if (hiddenGroups && hiddenGroups.length) {
                allGroups = allGroups.filter(o => hiddenGroups.find(v => v === o.objectID) === undefined);
            }

            const data = allGroups.find(o => o.objectID === groupID);

            if (data) {
                getChildren(allGroups, data);
            }

            setGroups(allGroups);

            setTreeData(data);
        };

        if ((userState.customer && userState.user?.baseGroup?.id) || offlineState.offlineDataLoaded) {
            getTreeData(userState.customer?.id as number, 
                        rootID ?? userState.user?.baseGroup?.id as number);
        }



        const onObjectChanged = (objectChangedData: ObjectChangedMsg) => {

            //console.log('GroupHierarchy - onObjectChanged');

            // the changed object is a group and the name is not set, then the change likely came from
            // group admin so rebuild the breadcrumb
            // if (objectChangedData.entity.objectType === eLynxObjectTypeEnum.Group &&
            //     objectChangedData.name === undefined) {
            if (objectChangedData.entity.objectType === eLynxObjectTypeEnum.Group) {
                    getTreeData(userState.customer?.id as number, 
                        userState.user?.baseGroup?.id as number);
            }
        }

        const token = subscribe(OBJECT_CHANGED_TOPIC, (message, data) => onObjectChanged(data));

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }


    }, [userState.customer,
        userState.user?.baseGroup?.id,
        offlineState,
        setGroups,
        hiddenGroups,
        rootID
    ]);

    useEffect(() => {

        if (onLoading) {
            onLoading(treeData === undefined);
        }
    }, [treeData,
        onLoading]);    

    useEffect(() => {

        const getItemsToExpand = async (customerID: number, groupID: number) => {

            let itemsToExpand: string[] = [];

            const contextGroup = await NavigationDataService.getNavigationGroup(customerID,
                groupID, {
                includeChildren: true,
                includeAncestors: true
            });

            if (contextGroup?.ancestors) {
                itemsToExpand = contextGroup?.ancestors?.map(o => o.objectID.toString());
            }

            itemsToExpand.push(groupID.toString());
            setExpanded(itemsToExpand.reverse());
        };

        const groupID = defaultSelected ?? routeContext.groupID; 
        if (expanded.length === 0 && groupID) {
            getItemsToExpand(userState.customer?.id as number, groupID);
        }

    }, [groups,
        userState.customer,
        routeContext.groupID,
        defaultSelected,
        expanded,
        setExpanded,
        onExpanded]);

    useEffect(() => {
        const item = defaultSelected?.toString() ?? routeContext.groupID?.toString();

        if (item !== selected) {
            setSelected(item);
        }
    }, [
        routeContext.groupID, 
        selected, 
        setSelected,
        defaultSelected
    ]);


    function onExpandedItemsChange(_event: React.SyntheticEvent | null, itemIds: TreeViewItemId[]) {
        setExpanded(itemIds);

        onExpanded?.();
    };

    const onLabelClick = (event: SyntheticEvent<Element, MouseEvent> | null, item: INavigationItem) => {        

        if (item.allowClick) {
            onSelected(item, event?.nativeEvent?.shiftKey ?? false, event?.nativeEvent?.ctrlKey ?? false);
            setSelected(item.objectID.toString());
        }

        event?.preventDefault();
    };

    // 2021-02-08, cannot use load on demand with the tree component, it has trouble with the expanded state
    // function updateTreeItem(item: INavigationItem, newItem: INavigationItem) : string[] { 
    
    //     let path: string[] = [];

    //     if (item.objectID === newItem.objectID) {

    //         path.push(item.objectID.toString());
    //         item.children = newItem.children;
    //     }

    //     if (item.hasChildren && item.children) {

    //         for (let i = 0; i < item.children.length; ++i) {

    //             const childItem = item.children[i];
                
    //             const subTreePath: string[] = updateTreeItem(childItem, newItem);

    //             if (subTreePath.length) {
    //                 if (!subTreePath.find(o => o === childItem.objectID.toString())) {
    //                     path.push(childItem.objectID.toString());
    //                 }

    //                 path = path.concat(subTreePath);
    //                 break;
    //             }
    //         }
    //     }

    //     return path;
    // }
    // const onLoadTreeItem = async (event: React.MouseEvent<Element, MouseEvent>, item: INavigationItem) => {

    //     if (item.hasChildren) {

    //         const group = await NavigationDataService.getNavigationGroup({
    //             customerID: appSettings.userState.customer?.id as number,
    //             objectID: item.objectID,
    //             includeChildren: true
    //         }) as INavigationItem;

    //         let newTreeData = {...treeData} as INavigationItem;

    //         //console.log(JSON.stringify(expanded));

    //         // get new expanded list, including what was already expanded
    //         let path = updateTreeItem(newTreeData, group).concat(expanded);

    //         // make sure no duplicates
    //         path = path.filter((item, index) => path.indexOf(item) === index);

    //         setExpanded(path.reverse());
    //         setTreeData(newTreeData);
    //     }

    //     setSelected(routeContext.groupID?.toString());
    //     event.preventDefault();
    // }

    const renderTree = (item: INavigationItem) => (
        <GroupTreeItem key={item.objectID}
            itemId={item.objectID.toString()}
            showIcon={true}
            // 2021-02-08 tried load on demand, but, tree does not handle state updates correctly, specifically the expanded state
            //endIcon={item.hasChildren && <Box component='span' className={classes.toggleIcon} onClick={(e) => onLoadTreeItem(e, item)}><ChevronRightIcon /></Box>}
            navigationItem={item}
            includeObjectCount={true}
            enableDynamicGroupRefresh={enableDynamicGroupRefresh && !offlineState.offline}
            onLabelClick={item.allowClick ? (e: React.MouseEvent<Element, MouseEvent>) => onLabelClick(e, item) : undefined}
        >
            {
                Array.isArray(item.children) ?
                    item.children.map((node) => renderTree(node)) :
                    null
            }
        </GroupTreeItem>
    );
    
      return (
          <SimpleTreeView
              ref={treeViewRef}
              sx={{
                  flexGrow: 1,
                  width: 400,
                  [theme.breakpoints.only('xs')]: {
                    width: '100%'
                  }
              }}
              slots={{
                  collapseIcon: ExpandMoreIcon,
                  expandIcon: ChevronRightIcon
              }}
              expandedItems={expanded}
              selectedItems={selected}
              onExpandedItemsChange={onExpandedItemsChange}
          >
              {
                treeData && renderTree(treeData)
              }
          </SimpleTreeView>
      );
  }
